import React, { lazy, Suspense, useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import BottomNav from './components/Layout/BottomNav';
import NudgeBanner from './components/Layout/NudgeBanner';
import TrialBanner from './components/Subscription/TrialBanner';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import { createPaymentAdapter } from './services/payment/index';
import { startAuthRefreshScheduler } from './services/authRefresh';
import { queryClient } from './lib/queryClient';
import { useAppStore } from './store/useAppStore';
import TimerPage from './pages/TimerPage';

const GardenPage = lazy(() => import('./pages/GardenPage'));
const StatsPage = lazy(() => import('./pages/StatsPage'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const AchievementsPage = lazy(() => import('./pages/AchievementsPage'));
const AlmanacPage = lazy(() => import('./pages/AlmanacPage'));

function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[300px] h-full">
      <div className="w-9 h-9 rounded-full border-[2.5px] border-tomato-500/10 border-t-tomato-500 animate-spin-slow" role="status" aria-label="Loading" />
    </div>
  );
}

function ThemeSync() {
  const nightMode = useAppStore((s) => s.nightMode);
  const setNightMode = useAppStore((s) => s.setNightMode);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', nightMode);
  }, [nightMode]);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => setNightMode(e.matches);
    if (!useAppStore.persist.hasHydrated || !useAppStore.persist.hasHydrated()) {
      setNightMode(mq.matches);
    }
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [setNightMode]);

  return null;
}

function AppContent() {
  const [timerState, setTimerState] = useState('idle');
  const location = useLocation();
  const isActiveSession = timerState === 'running' || timerState === 'completed' || timerState === 'killed' || timerState === 'break';
  const hideNav = isActiveSession && location.pathname === '/';

  return (
    <div className="flex flex-col min-h-screen min-h-[100dvh] safe-area-top relative">
      <NudgeBanner />
      <TrialBanner />
      <main className="flex-1 overflow-y-auto overflow-x-hidden [-webkit-overflow-scrolling:touch]">
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<TimerPage onStateChange={setTimerState} />} />
            <Route path="/garden" element={<GardenPage />} />
            <Route path="/stats" element={<StatsPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/achievements" element={<AchievementsPage />} />
            <Route path="/almanac" element={<AlmanacPage />} />
          </Routes>
        </Suspense>
      </main>
      <BottomNav hidden={hideNav} />
    </div>
  );
}

export default function App() {
  const [paymentAdapter, setPaymentAdapter] = useState(null);

  useEffect(() => {
    createPaymentAdapter()
      .then(setPaymentAdapter)
      .catch((err) => console.warn('Payment adapter init failed:', err));
  }, []);

  useEffect(() => startAuthRefreshScheduler(), []);

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ThemeSync />
        <SubscriptionProvider paymentAdapter={paymentAdapter}>
          <AppContent />
        </SubscriptionProvider>
        <Toaster
          position="top-center"
          containerStyle={{ top: 'calc(env(safe-area-inset-top, 0px) + 8px)' }}
          toastOptions={{
            duration: 3500,
            style: {
              borderRadius: '14px',
              padding: '12px 16px',
              fontSize: '14px',
              fontWeight: 500,
              maxWidth: '92vw',
              background: 'var(--toast-bg)',
              color: 'var(--toast-fg)',
              boxShadow: '0 8px 30px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.10)',
              border: '1px solid var(--toast-border)',
            },
            success: { iconTheme: { primary: 'var(--app-accent)', secondary: '#fff' }, duration: 2500 },
            error: { iconTheme: { primary: '#ef4444', secondary: '#fff' }, duration: 4500 },
          }}
        />
      </BrowserRouter>
    </QueryClientProvider>
  );
}
